import { useCallback, memo, useMemo, ReactElement } from 'react'
import { Trade, Currency, TradeType, CurrencyAmount, ChainId } from '@pancakeswap/sdk'
import { InjectedModalProps } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { Field } from 'state/swap/actions'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { getBlockExploreLink, getBlockExploreName } from 'utils'
import Link from 'next/link'
import TransactionConfirmSwapContent from './TransactionConfirmSwapContent'
import ConfirmSwapModalContainer from './ConfirmSwapModalContainer'
import { StableTrade } from '../StableSwap/hooks/useStableTradeExactIn'

export function ConfirmationPendingContent({ pendingText }: { pendingText?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 h-[300px] w-[300px]">
      <div className="h-24 w-24 flex items-center justify-center">
        <span className="text-6xl font-bold">🤔</span>
      </div>
      <div className="flex flex-col items-center justify-center gap-2  text-orange-200">
        <div className="text-balance text-center">{pendingText || 'Waiting for confirmation...'}</div>
        <div className="opacity-60 text-sm"> Confirm this transaction in your wallet</div>
      </div>
    </div>
  )
}

export function TransactionErrorContent({ message }: { message: ReactElement | string; onDismiss?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 h-[300px] w-[300px]">
      <div className="h-24 w-24 flex items-center justify-center">
        <span className="text-6xl font-bold">🤯</span>
      </div>
      <div className="flex flex-col items-center justify-center gap-2  text-orange-200">
        <div className="text-balance text-center">{message}</div>
      </div>
    </div>
  )
}

export function TransactionSubmittedContent({
  chainId,
  hash,
}: {
  onDismiss: () => void
  hash: string | undefined
  chainId: ChainId
  currencyToAdd?: Currency | undefined
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-6 h-[300px] w-[300px]">
      <div className="h-24 w-24 flex items-center justify-center">
        <span className="text-6xl font-bold">🥳</span>
      </div>
      <div className="flex flex-col items-center justify-center gap-2  text-orange-200">
        <div className="">Transaction Submitted</div>
        {chainId && hash && (
          <Link target="_blank" href={getBlockExploreLink(hash, 'transaction', chainId)}>
            <span className="text-sm opacity-60">{`View on ${getBlockExploreName(chainId)}`}</span>
          </Link>
        )}
      </div>
    </div>
  )
}

const PancakeRouterSlippageErrorMsg =
  'This transaction will not succeed either due to price movement or fee on transfer. Try increasing your slippage tolerance.'

const SwapTransactionErrorContent = ({ onDismiss, message, openSettingModal }) => {
  const isSlippagedErrorMsg = message?.includes(PancakeRouterSlippageErrorMsg)

  const handleErrorDismiss = useCallback(() => {
    onDismiss?.()
    if (isSlippagedErrorMsg && openSettingModal) {
      openSettingModal()
    }
  }, [isSlippagedErrorMsg, onDismiss, openSettingModal])
  const { t } = useTranslation()

  return isSlippagedErrorMsg ? (
    <TransactionErrorContent message={PancakeRouterSlippageErrorMsg} />
  ) : (
    <TransactionErrorContent message={message} onDismiss={onDismiss} />
  )
}

interface ConfirmSwapModalProps {
  trade?: Trade<Currency, Currency, TradeType> | StableTrade
  originalTrade?: Trade<Currency, Currency, TradeType> | StableTrade
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  attemptingTxn: boolean
  txHash?: string
  recipient: string | null
  allowedSlippage: number
  onAcceptChanges: () => void
  onConfirm: () => void
  swapErrorMessage?: string
  customOnDismiss?: () => void
  openSettingModal?: () => void
  isStable?: boolean
}

const ConfirmSwapModal: React.FC<React.PropsWithChildren<InjectedModalProps & ConfirmSwapModalProps>> = ({
  trade,
  originalTrade,
  currencyBalances,
  onAcceptChanges,
  allowedSlippage,
  onConfirm,
  onDismiss,
  customOnDismiss,
  recipient,
  swapErrorMessage,
  attemptingTxn,
  txHash,
  openSettingModal,
  isStable,
}) => {
  const { chainId } = useActiveChainId()
  const { t } = useTranslation()

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      customOnDismiss()
    }
    onDismiss?.()
  }, [customOnDismiss, onDismiss])

  const confirmationContent = useCallback(
    () =>
      swapErrorMessage ? (
        <SwapTransactionErrorContent
          openSettingModal={openSettingModal}
          onDismiss={onDismiss}
          message={swapErrorMessage}
        />
      ) : (
        <TransactionConfirmSwapContent
          isStable={isStable}
          trade={trade}
          currencyBalances={currencyBalances}
          originalTrade={originalTrade}
          onAcceptChanges={onAcceptChanges}
          allowedSlippage={allowedSlippage}
          onConfirm={onConfirm}
          recipient={recipient}
        />
      ),
    [
      isStable,
      trade,
      originalTrade,
      onAcceptChanges,
      allowedSlippage,
      onConfirm,
      recipient,
      swapErrorMessage,
      onDismiss,
      openSettingModal,
      currencyBalances,
    ],
  )

  // text to show while loading
  const pendingText = useMemo(() => {
    return t('Swapping %amountA% %symbolA% for %amountB% %symbolB%', {
      amountA: trade.inputAmount?.toSignificant(6) ?? '',
      symbolA: trade.inputAmount?.currency?.symbol ?? '',
      amountB: trade.outputAmount?.toSignificant(6) ?? '',
      symbolB: trade.outputAmount?.currency?.symbol ?? '',
    })
  }, [t, trade])

  if (!chainId) return null

  return (
    <ConfirmSwapModalContainer handleDismiss={handleDismiss}>
      {attemptingTxn ? (
        <ConfirmationPendingContent pendingText={pendingText} />
      ) : txHash ? (
        <TransactionSubmittedContent
          chainId={chainId}
          hash={txHash}
          onDismiss={handleDismiss}
          currencyToAdd={trade?.outputAmount.currency}
        />
      ) : (
        confirmationContent()
      )}
    </ConfirmSwapModalContainer>
  )
}

export default memo(ConfirmSwapModal)
